import { useEffect } from "react"
import { useForm, useWatch } from "react-hook-form"
import { useDrawer } from "@contexts/DrawerContext"
import { useUtils } from "../utils/utils"
import {
  useGetRevenueClassesQuery,
  useGetBusinessTitlesQuery,
  useGetAccountHolderRelationshipQuery,
} from "@apis/shared/useShared"
import { useCreateShopperMutation } from "@apis/shoppers/useShoppers"
import { toast } from "sonner"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import { Input } from "@components/ui/input"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { Button } from "@components/ui/button"
import { AddressInfoForm } from "@components/Address/AddressInfoForm"
import type { IProspect } from "@commonTypes/prospect"
import type { IOffer } from "@commonTypes/offer"
import type { IAddress } from "@commonTypes/address"
import { useNewOrderFunnel } from "../utils/useNewOrderFunnel"
import { TpvTypesSelect } from "@components/AccountNew/OfferFunnel/TpvTypeSelect"
import { Loader2 } from "lucide-react"

export type TSignUpFormValues = {
  email: string
  primary_phone: string
  first_name: string
  last_name: string
  not_account_holder: boolean
  contact_last_name?: string
  contact_first_name?: string
  contact_relationships?: string
  business_name?: string
  business_title?: string
  business_attention_line?: string
  service_address_id?: number
  mailing_address_id?: number
  revenue_class?: string
  account_number?: string
  offer_code?: string
  verification_method?: string
  marketing_opt_in: boolean
}

type SignupFormProps = {
  prospect?: IProspect
  selectedOffer: IOffer
  selectedAddress?: IAddress
  revenueClassCode?: string
  onNext: (data: TSignUpFormValues) => void
  onCancel: () => void
}

export const SignupForm = ({
  prospect,
  selectedOffer,
  selectedAddress,
  revenueClassCode,
  onNext,
  onCancel,
}: SignupFormProps) => {
  const { shopper, setShopper, tpv, setTpv, selectedMarket } =
    useNewOrderFunnel()
  const getRevenueClassQuery = useGetRevenueClassesQuery()
  const getBusinessTitlesQuery = useGetBusinessTitlesQuery()
  const getAccountHolderRelationshipQuery =
    useGetAccountHolderRelationshipQuery()
  const createShopperMutation = useCreateShopperMutation()
  const { openDrawer, closeDrawer } = useDrawer()
  const { handleSaveAddress } = useUtils()

  const defaultServiceAddressId = selectedAddress?.address_id

  const form = useForm<TSignUpFormValues>({
    mode: "onTouched",
    defaultValues: {
      email:
        typeof prospect?.email === "string"
          ? prospect.email
          : prospect?.email?.email_address || "",
      primary_phone: prospect?.primary_phone || "",
      first_name: prospect?.first_name || "",
      last_name: prospect?.last_name || "",
      not_account_holder: false,
      contact_last_name: "",
      contact_first_name: "",
      contact_relationships: "",
      business_name: "",
      business_title: "",
      business_attention_line: "",
      service_address_id: defaultServiceAddressId,
      mailing_address_id: defaultServiceAddressId, // By default we use the same address_id for service and mailing
      revenue_class: revenueClassCode,
      account_number: shopper?.account_number || prospect?.account_number || "",
      offer_code: selectedOffer?.offer_code,
      verification_method: "",
      marketing_opt_in: true,
    },
  })

  // Keep offer_code synced
  useEffect(() => {
    if (
      selectedOffer?.offer_code &&
      form.getValues("offer_code") !== selectedOffer.offer_code
    ) {
      form.setValue("offer_code", selectedOffer.offer_code, {
        shouldDirty: false,
      })
    }
  }, [selectedOffer, form])

  const addressDisplay = selectedAddress
    ? `${selectedAddress.street} ${selectedAddress.city} ${selectedAddress.state_code} ${selectedAddress.zip}`
    : ""

  const revenueClass = useWatch({
    control: form.control,
    name: "revenue_class",
  })
  const notAccountHolder = useWatch({
    control: form.control,
    name: "not_account_holder",
  })
  const accountHolderRequired =
    selectedMarket?.account_holder_required_for_signup === true

  const buildPayloadFromForm = (data: TSignUpFormValues) => {
    if (!shopper || !selectedOffer) {
      toast.error("No Shopper or Offer found")
      return null
    }

    return {
      ...shopper,
      email: data.email,
      primary_phone: data.primary_phone,
      first_name: data.first_name,
      last_name: data.last_name,
      business_name: data.business_name || undefined,
      title: data.business_title || undefined,
      attention_line: data.business_attention_line || undefined,
      contact_first_name: data.contact_first_name || undefined,
      contact_last_name: data.contact_last_name || undefined,
      contact_relationships: data.contact_relationships || undefined,
      service_address_id: data.service_address_id || undefined,
      mailing_address_id: data.mailing_address_id || undefined,
      revenue_class_code: data.revenue_class || undefined,
      account_number: data.account_number,
      offer_code: data.offer_code,
      ack_can_text: data.marketing_opt_in,
      campaign_id: shopper.campaign_id,
      offer: selectedOffer,
      tpv_type: tpv?.vendor_type || null,
      tpv_vendor: tpv?.vendor_code || null,
      shopper_status: "SAVED",
    }
  }

  const submitShopper = (
    data: TSignUpFormValues,
    onSuccessCallback?: () => void,
  ) => {
    const payload = buildPayloadFromForm(data)
    if (!payload) return

    createShopperMutation.mutate(payload, {
      onSuccess: (shopper) => {
        setShopper(shopper)
        if (onSuccessCallback) {
          onSuccessCallback()
        } else {
          toast.success("Shopper saved successfully") // Inform user only when Saving as partial
        }
      },
      onError: (error) => {
        toast.error(`Error saving  shopper! Error: ${error}`)
      },
    })
  }

  const handleNext = (data: TSignUpFormValues) => {
    submitShopper(data, () => onNext(data))
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleNext)}
        className="tw:flex tw:flex-col tw:gap-6"
      >
        <div className="tw:grid tw:grid-cols-12 tw:gap-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="tw:col-span-6">
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="name@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="primary_phone"
            rules={{
              required: "Primary phone is required",
              pattern: {
                value: /^\d{10}$/,
                message: "Phone must be a 10-digit number",
              },
            }}
            render={({ field }) => (
              <FormItem className="tw:col-span-6">
                <FormLabel>Phone number</FormLabel>
                <FormControl>
                  <Input placeholder="Enter phone number" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <FormItem className="tw:col-span-6">
                <FormLabel>Account First Name</FormLabel>
                <FormControl>
                  <Input placeholder="Account First name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <FormItem className="tw:col-span-6">
                <FormLabel>Account Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="Account Last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {accountHolderRequired ? (
            <div className="tw:col-span-12">
              <p className="tw:text-xs tw:font-bold">
                In MD, only an account holder can enroll.
              </p>
            </div>
          ) : (
            <FormField
              control={form.control}
              name="not_account_holder"
              render={({ field }) => (
                <FormItem className="tw:col-span-12">
                  <FormControl>
                    <div className="tw:flex tw:items-start tw:gap-2">
                      <span className="tw:text-xs tw:font-bold">
                        Not speaking with the account holder?
                      </span>
                      <input
                        type="checkbox"
                        className="tw:gap-1"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          {notAccountHolder && (
            <>
              <FormField
                control={form.control}
                name="contact_first_name"
                render={({ field }) => (
                  <FormItem className="tw:col-span-6">
                    <FormLabel>Contact First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Contact First name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="contact_last_name"
                render={({ field }) => (
                  <FormItem className="tw:col-span-6">
                    <FormLabel>Contact Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Contact Last name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="contact_relationships"
                rules={{ required: "Relationship" }}
                render={({ field }) => (
                  <FormItem className="tw:col-span-6">
                    <FormLabel>Relationship to account holder</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value)
                      }}
                      value={field.value}
                    >
                      <FormControl className="tw:w-full">
                        <SelectTrigger>
                          <SelectValue placeholder="Select Relationship" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getAccountHolderRelationshipQuery?.data?.map(
                          (item) => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}

          {revenueClass === "SMALL-COM" && (
            <>
              <FormField
                control={form.control}
                name="business_name"
                render={({ field }) => (
                  <FormItem className="tw:col-span-12">
                    <FormLabel>Business Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Business Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="business_title"
                rules={{ required: "Title" }}
                render={({ field }) => (
                  <FormItem className="tw:col-span-6">
                    <FormLabel>Title</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value)
                      }}
                      value={field.value}
                    >
                      <FormControl className="tw:w-full">
                        <SelectTrigger>
                          <SelectValue placeholder="Select Title" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getBusinessTitlesQuery?.data?.map((item) => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="business_attention_line"
                render={({ field }) => (
                  <FormItem className="tw:col-span-6">
                    <FormLabel>Attention Line</FormLabel>
                    <FormControl>
                      <Input placeholder="Attention Line" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}

          <hr className="tw:col-span-12" />
          <FormField
            control={form.control}
            name="service_address_id"
            render={() => (
              <FormItem className="tw:col-span-9">
                <FormLabel>Service Address</FormLabel>
                <FormControl>
                  <Input disabled value={addressDisplay} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="revenue_class"
            rules={{ required: "Revenue Class" }}
            render={({ field }) => (
              <FormItem className="tw:col-span-9">
                <FormLabel>Revenue Class</FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value)
                  }}
                  value={field.value}
                >
                  <FormControl className="tw:w-full">
                    <SelectTrigger>
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {getRevenueClassQuery?.data?.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="mailing_address_id"
            rules={{ required: "Mailing Address is required" }}
            render={({ field }) => (
              <FormItem className="tw:col-span-9">
                <FormLabel>Mailing Address</FormLabel>
                <div className="tw:flex tw:gap-2">
                  <Select
                    value={field.value?.toString() ?? ""}
                    onValueChange={(value) => {
                      field.onChange(value)
                    }}
                  >
                    <FormControl className="tw:w-full">
                      <SelectTrigger>
                        <SelectValue placeholder="Select mailing address" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {prospect?.addresses?.map((address) => (
                        <SelectItem
                          key={address.address_id}
                          value={address.address_id.toString()}
                        >
                          {address.street}, {address.city}, {address.state_code}{" "}
                          {address.zip}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    size="sm"
                    variant="default"
                    onClick={() =>
                      openDrawer(
                        <AddressInfoForm
                          identifier={prospect?.prospect_guid as string}
                          address_type_code="prospect"
                          onSave={() =>
                            handleSaveAddress(
                              prospect?.prospect_guid as string,
                              closeDrawer,
                            )
                          }
                          onCancel={() => closeDrawer()}
                        />,
                        "Address Information",
                      )
                    }
                  >
                    Add Address
                  </Button>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <hr className="tw:col-span-12" />
          <FormField
            control={form.control}
            name="account_number"
            rules={{
              required: `${selectedOffer.account_num_synonym} is required`,
              pattern: selectedOffer.validation_rules?.account_number_regexp
                ? {
                    value: new RegExp(
                      selectedOffer.validation_rules.account_number_regexp,
                    ),
                    message: `Invalid ${selectedOffer.account_num_synonym} format. It should be ${selectedOffer.validation_rules.account_number_example}`,
                  }
                : undefined,
            }}
            render={({ field }) => (
              <FormItem className="tw:col-span-9">
                <FormLabel>{selectedOffer.account_num_synonym}</FormLabel>
                <div className="tw:flex">
                  <FormControl className="tw:flex-1">
                    <Input {...field} />
                  </FormControl>
                  <p className="tw:place-content-center tw:rounded-r tw:bg-gray-100 tw:px-4 tw:text-gray-700">
                    {selectedOffer.validation_rules.account_number_example}
                  </p>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="verification_method"
            render={() => (
              <FormItem className="tw:col-span-9">
                <FormLabel>Verification Method</FormLabel>
                <TpvTypesSelect
                  placeholder="Select method"
                  tpvType={tpv?.vendor_type || null}
                  setTpv={setTpv}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="marketing_opt_in"
            render={({ field }) => (
              <FormItem className="tw:col-span-12">
                <FormLabel className="tw:text-xs">Marketing Consent</FormLabel>
                <FormControl>
                  <div className="tw:flex tw:items-start tw:gap-2">
                    <input
                      type="checkbox"
                      className="tw:mt-1"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                    <span className="tw:text-xs tw:font-bold">
                      Please confirm that you agree to receive autodialed and/or
                      pre-recorded service-related and promotional calls and
                      texts from Inspire, which may vary in frequency, at the
                      phone number provided above. Standard carrier charges may
                      apply. You do not have to agree to receive calls or texts
                      on your mobile phone to purchase Inspire’s products and
                      services, and you can revoke my consent any time by
                      calling 866-403-2620 or by replying STOP to any text
                      message.
                    </span>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:flex tw:flex-wrap tw:gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              const data = form.getValues()
              submitShopper(data)
            }}
            disabled={createShopperMutation.isPending}
          >
            {createShopperMutation.isPending ? (
              <>
                <Loader2 className="tw:animate-spin" />
                <span>Saving Partial...</span>
              </>
            ) : (
              <span>Save as Partial</span>
            )}
          </Button>
          <Button type="submit">Next</Button>
        </div>
      </form>
    </Form>
  )
}
